// 應收帳款中心: 未收總覽 + 自動對帳單
import React, { useEffect, useState } from 'react';
import { api, auth, money, fmtDate, todayStr, INV_STATUS, r2 } from '../api.js';
import { Modal, Field, StatusBadge, Loading, Empty, PageHead, useToast } from '../ui.jsx';

function Statement({ customerId, onClose, onPaid }) {
  const { toast } = useToast();
  const [d, setD] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [f, setF] = useState({ pay_date: todayStr(), method: 'bank', amount: 0 });
  const load = () => api.get(`/ar/statement/${customerId}`).then(setD);
  useEffect(() => { load(); }, [customerId]);
  if (!d) return null;
  const today = todayStr();
  const toPDF = async () => {
    try {
      toast('正在產生對帳單 PDF…');
      const resp = await fetch('/api/customers/' + customerId + '/statement/generate-pdf', { headers: { Authorization: 'Bearer ' + auth.token } });
      if (!resp.ok) throw new Error('PDF 生成失敗 (' + resp.status + ')');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'Statement-' + (d.customer.name || '') + '-' + today + '.pdf'; a.click();
      URL.revokeObjectURL(url);
      toast('PDF 已下載 ✔');
    } catch (e) { toast(e.message, 'err'); }
  };
  const openPay = (inv) => { setPayFor(inv); setF(x => ({ ...x, amount: r2(inv.adjusted_amount - inv.paid_amount) })); };
  const doPay = async () => {
    try { await api.post(`/invoices/${payFor.id}/payments`, f); toast('已記錄收款 ✔'); setPayFor(null); load(); onPaid(); }
    catch (e) { toast(e.message, 'err'); }
  };
  const doClear = async (inv) => {
    try { await api.post(`/invoices/${inv.id}/clear`, { pay_date: f.pay_date, method: 'full' }); toast('已結清 ✔'); load(); onPaid(); }
    catch (e) { toast(e.message, 'err'); }
  };
  return (
    <Modal size="xl" onClose={onClose} icon="💰" title={`月結對帳單 — ${d.customer.name}`}
      footer={<>
        <button className="btn" onClick={onClose}>關閉</button>
        <button className="btn" onClick={async () => {
          try { toast('生成對帳單 PDF…'); const r = await api.post('/customers/' + customerId + '/statement/send', { channel: 'whatsapp' }); if (r.url) window.open(r.url, '_blank'); toast('WhatsApp 已準備 — PDF 連結已附上 ✔'); }
          catch (e) { toast(e.message, 'err'); }
        }}>📱 WhatsApp</button>
        <button className="btn" onClick={async () => {
          try { toast('生成對帳單 PDF…'); const r = await api.post('/customers/' + customerId + '/statement/send', { channel: 'email' }); if (r.url) window.open(r.url, '_blank'); toast('Email 已準備 — PDF 連結已附上 ✔'); }
          catch (e) { toast(e.message, 'err'); }
        }}>✉️ Email</button>
        <button className="btn primary" onClick={toPDF}>📥 下載對帳單 PDF</button>
      </>}>
      <div className="stat-grid" style={{ marginBottom: 14 }}>
        <div className="stat-card" style={{ padding: 14 }}>
          <span className="ico-badge coral">💰</span>
          <div><div className="stat-val" style={{ fontSize: 20 }}><span className="cur">HK$</span>{money(d.total_outstanding)}</div><div className="stat-label">總欠款 Total Outstanding</div></div>
        </div>
        {[
          ['未到期', d.aging.current, ''], ['1-30日', d.aging.d30, ''], ['31-60日', d.aging.d60, ''],
          ['61-90日', d.aging.d90, ''], ['90日+', d.aging.over, ''],
        ].map(([k, v]) => (
          <div key={k} className="stat-card" style={{ padding: 14 }}>
            <div>
              <div className="stat-val" style={{ fontSize: 17, color: v > 0 && k !== '未到期' ? 'var(--coral)' : 'var(--ink)' }}>{money(v)}</div>
              <div className="stat-label">{k}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="tbl-wrap">
        <table className="tbl">
          <thead><tr><th>發票</th><th>訂單</th><th>項目</th><th>到期</th><th className="num">應收</th><th className="num">已收</th><th className="num">餘數</th><th></th></tr></thead>
          <tbody>
            {d.invoices.map(inv => {
              const remain = r2(inv.adjusted_amount - inv.paid_amount);
              const overdue = inv.due_date && inv.due_date < today;
              return (
                <tr key={inv.id}>
                  <td className="strong">{inv.invoice_no}</td>
                  <td className="sub">{inv.order_no || '—'}</td>
                  <td>{inv.installment_label || inv.order_title || '—'}</td>
                  <td style={{ color: overdue ? 'var(--coral)' : undefined }}>{overdue ? '⚠️ ' : ''}{fmtDate(inv.due_date)}</td>
                  <td className="num">{money(inv.adjusted_amount)}</td>
                  <td className="num">{money(inv.paid_amount)}</td>
                  <td className="num strong" style={{ color: overdue && remain > 0 ? 'var(--coral)' : undefined }}>{money(remain)}</td>
                  <td>
                    <div className="row" style={{ flexWrap: 'nowrap' }}>
                      <button className="btn sm mint" onClick={() => doClear(inv)}>✅ 結清</button>
                      <button className="btn sm" onClick={() => openPay(inv)}>💵 部分收</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {d.payments.length ? (
        <div className="mt">
          <div className="card-title"><span className="ico-badge mint" style={{ width: 28, height: 28, fontSize: 13, borderRadius: 8 }}>✅</span>最近收款</div>
          {d.payments.map(p => (
            <div key={p.id} className="row" style={{ justifyContent: 'space-between', fontSize: 12.5, padding: '6px 4px', borderBottom: '1px dashed var(--line)' }}>
              <span>{fmtDate(p.pay_date)} · {p.invoice_no} · {p.method || ''}</span><strong>+ HK$ {money(p.amount)}</strong>
            </div>
          ))}
        </div>
      ) : null}
      {payFor && (
        <Modal onClose={() => setPayFor(null)} icon="💵" title={`部分收款 — ${payFor.invoice_no}`}
          footer={<><button className="btn" onClick={() => setPayFor(null)}>取消</button><button className="btn primary" onClick={doPay}>💾 確定</button></>}>
          <div className="form-grid">
            <Field label="收款日期"><input className="input" type="date" value={f.pay_date} onChange={e => setF({ ...f, pay_date: e.target.value })} /></Field>
            <Field label="金額 (未收 {money(payFor.adjusted_amount - payFor.paid_amount)})">
              <input className="input num-input" type="number" step="0.01" value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} />
            </Field>
            <Field label="方式">
              <select className="select" value={f.method} onChange={e => setF({ ...f, method: e.target.value })}>
                <option value="bank">銀行轉帳</option><option value="cheque">支票</option><option value="cash">現金</option><option value="other">其他</option>
              </select>
            </Field>
          </div>
        </Modal>
      )}
    </Modal>
  );
}

export default function AR() {
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [stmtId, setStmtId] = useState(null);
  const [q, setQ] = useState('');
  const refresh = () => {
    api.get('/ar/summary').then(setSummary);
    api.get('/ar/invoices').then(setInvoices);
  };
  useEffect(() => { refresh(); }, []);
  const totalOut = (summary || []).reduce((s, x) => s + x.outstanding, 0);
  const totalOver = (summary || []).reduce((s, x) => s + x.overdue, 0);
  const kw = q.trim().toLowerCase();
  const fSummary = (summary || []).filter(c => !kw || c.customer_name.toLowerCase().includes(kw));
  const fInvoices = (invoices || []).filter(i => !kw || i.customer_name.toLowerCase().includes(kw) || (i.invoice_no || '').toLowerCase().includes(kw));
  const qOut = fSummary.reduce((s, x) => s + x.outstanding, 0);

  return (
    <div>
      <PageHead icon="💰" title="應收帳款中心 A/R" desc="所有客戶未找數發票自動匯總 · 撳客戶名開對帳單 (Statement)"
        actions={
          <div className="search-box" style={{ minWidth: 300 }}>
            <input className="input" placeholder="🔍 搜尋客戶 — 睇下佢爭公司幾多錢…" value={q} onChange={e => setQ(e.target.value)} />
          </div>
        } />
      {kw ? (
        <div className="row mb" style={{ gap: 10 }}>
          <span className="st st-blue">搜尋「{q.trim()}」— {fSummary.length} 個客戶, 合共欠款 HK$ {money(qOut)}</span>
          <button className="btn sm" onClick={() => setQ('')}>✕ 清除</button>
        </div>
      ) : null}
      <div className="stat-grid">
        <div className="stat-card"><span className="ico-badge coral">💰</span><div>
          <div className="stat-val"><span className="cur">HK$</span>{money(totalOut)}</div><div className="stat-label">總應收未收</div></div></div>
        <div className="stat-card"><span className="ico-badge amber">⚠️</span><div>
          <div className="stat-val" style={{ color: 'var(--coral)' }}><span className="cur">HK$</span>{money(totalOver)}</div><div className="stat-label">已逾期款項</div></div></div>
        <div className="stat-card"><span className="ico-badge">🏢</span><div>
          <div className="stat-val">{(summary || []).length}</div><div className="stat-label">欠款客戶數</div></div></div>
        <div className="stat-card"><span className="ico-badge sky">🧾</span><div>
          <div className="stat-val">{(invoices || []).length}</div><div className="stat-label">未清發票張數</div></div></div>
      </div>

      <div className="card">
        <div className="card-title"><span className="ico-badge coral" style={{ width: 28, height: 28, fontSize: 13, borderRadius: 8 }}>📊</span>客戶欠款排行 — 撳一行開對帳單
        {kw ? <span className="st st-blue" style={{marginLeft:8}}>{fSummary.length} 個結果</span> : null}</div>
        {summary === null ? <Loading /> : fSummary.length === 0 ? <Empty icon="🔍" text={q.trim() ? "搵唔到符合嘅客戶" : "全部客戶已找數!"} /> : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead><tr><th>客戶</th><th className="num">未清發票</th><th className="num">應收總額</th><th className="num">逾期</th><th className="num">未收總數</th><th></th></tr></thead>
              <tbody>
                {fSummary.map(c => (
                  <tr key={c.customer_id} className="clickable" onClick={() => setStmtId(c.customer_id)}>
                    <td>
                      <div className="row" style={{ gap: 10, flexWrap: 'nowrap' }}>
                        <span className="avatar">{c.customer_name.slice(0, 1)}</span>
                        <div className="strong">{c.customer_name}<div className="sub">{c.phone}</div></div>
                      </div>
                    </td>
                    <td className="num">{c.invoice_count}</td>
                    <td className="num">{money(c.total_amount)}</td>
                    <td className="num" style={{ color: c.overdue > 0 ? 'var(--coral)' : undefined }}>{money(c.overdue)}</td>
                    <td className="num strong" style={{ fontSize: 15 }}>{money(c.outstanding)}</td>
                    <td><button className="btn sm primary">📋 對帳單</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-title"><span className="ico-badge sky" style={{ width: 28, height: 28, fontSize: 13, borderRadius: 8 }}>🧾</span>全部未清發票 (按到期日)</div>
        {invoices === null ? <Loading /> : fInvoices.length === 0 ? <Empty icon="🔍" text={q.trim() ? '搵唔到符合嘅發票' : '無未清發票'} /> : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead><tr><th>發票</th><th>客戶</th><th>到期</th><th className="num">應收</th><th className="num">已收</th><th className="num">餘數</th><th>狀態</th></tr></thead>
              <tbody>
                {fInvoices.map(i => (
                  <tr key={i.id} className="clickable" onClick={() => setStmtId(i.customer_id)}>
                    <td className="strong">{i.invoice_no}</td>
                    <td>{i.customer_name}</td>
                    <td>{fmtDate(i.due_date)}</td>
                    <td className="num">{money(i.adjusted_amount)}</td>
                    <td className="num">{money(i.paid_amount)}</td>
                    <td className="num strong">{money(r2(i.adjusted_amount - i.paid_amount))}</td>
                    <td><StatusBadge map={INV_STATUS} value={i.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {stmtId && <Statement customerId={stmtId} onClose={() => setStmtId(null)} onPaid={refresh} />}
    </div>
  );
}
